import React, { useState, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'sonner';
import contactService from '@/services/contact/contactService';
import { useDropzone } from 'react-dropzone';
import { FormatFileSize } from '@/utils';

const ContactMessagesTab = ({ contactData, contactID }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);           // valid only
  const [rejectedFiles, setRejectedFiles] = useState([]);           // [{ file, reasons: string[] }]
  const [sendType, setSendType] = useState('email');
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null); // for clearing native input if ever used
  const formikRef = useRef(null);

  // Limits / Accepts
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB per file
  const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100 MB total

  const ALLOWED_MIME = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.ms-excel',                                                // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',       // .xlsx
  ];
  const ALLOWED_EXT = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'];

  const acceptMap = {
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  };

  // Helpers
  const dedupeByNameAndSize = (files) => {
    const seen = new Set();
    return files.filter(f => {
      const key = `${f.name}__${f.size}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const setDocError = (msg) => {
    formikRef?.current?.setFieldTouched('file', true, false);
    formikRef?.current?.setFieldError('file', msg);
  };

  const totalSizeOf = (files) => files.reduce((acc, f) => acc + (f?.size || 0), 0);

  const isAllowedType = (file) => {
    const ext = `.${(file.name?.split('.').pop() || '').toLowerCase()}`;
    return ALLOWED_MIME.includes(file.type) || ALLOWED_EXT.includes(ext);
  };

  // Formik
  const initialValues = {
    subject: '',
    message: '',
    sendType: 'email',
    file: [],
  };

  const validationSchema = Yup.object().shape({
    subject: Yup.string().required('Subject is required'),
    message: Yup.string().required('Message is required'),
    // File validation handled by onDrop (so we don't double-validate here)
    file: Yup.mixed().nullable(),
  });

  // Toggle Send Type
  const toggleSendType = (values, setFieldValue, setFieldTouched, validateField) => {
    const newType = values.sendType === 'email' ? 'message' : 'email';
    setFieldValue('sendType', newType);
    setFieldValue('file', []);
    setUploadedFiles([]);
    setRejectedFiles([]);

    if (fileInputRef.current) fileInputRef.current.value = '';

    setFieldTouched('file', true, false);
    validateField('file');
  };

  const onSubmit = async (formData, formikHelpers) => {
    const { resetForm } = formikHelpers;

    const Data = new FormData();
    Data.append('subject', formData.subject);
    Data.append('message', formData.message);
    Data.append('sendType', formData.sendType);

    if (formData.sendType === 'email' && formData.file?.length >= 1) {
      formData.file.forEach((file) => Data.append('file[]', file));
    }

    if (formData.sendType === 'email') {
      if (!contactData?.email?.trim()) {
        toast.warning('Missing Email Address', {
          description: 'This contact has no email. Please add one before proceeding.',
        });
        return;
      }
      Data.append('source', contactData.email);
    } else {
      // future SMS path
      Data.append('source', contactData?.phones?.[0]?.number || '');
    }

    setIsLoading(true);
    try {
      const { status } = await contactService.sendSecureMessage(Data);
      if (status) {
        toast.success('Message Sent Successfully', {
          description: `Your message has been sent to ${contactData.first_name} ${contactData.last_name} via ${formData.sendType}.`,
        });
        resetForm();
        setUploadedFiles([]);
        setRejectedFiles([]);
      }
    } catch (error) {
      console.error('Error', error);
      // if(error.message )
      toast.error('Opps! Something went wrong.', {
        description: 'An error occurred while sending message. Please try again later.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Dropzone
  // helper to merge rejected lists while merging reasons
  const mergeRejected = (existing, incoming) => {
    const map = new Map();
    [...existing, ...incoming].forEach(({ file, reasons = [] }) => {
      const key = `${file.name}__${file.size}`;
      const prev = map.get(key);
      const mergedReasons = new Set([...(prev?.reasons || []), ...reasons]);
      map.set(key, { file, reasons: Array.from(mergedReasons) });
    });
    return Array.from(map.values());
  };

  const onDrop = (acceptedFiles, fileRejections) => {
    const valid = [];
    const invalid = []; // { file, reasons: [] }

    acceptedFiles?.forEach((file) => {
      const ext = `.${(file.name?.split('.').pop() || '').toLowerCase()}`;
      const okType = ALLOWED_MIME.includes(file.type) || ALLOWED_EXT.includes(ext);
      const okSize = file.size <= MAX_FILE_SIZE;

      if (okType && okSize) valid.push(file);
      else {
        const reasons = [];
        if (!okType) reasons.push('Invalid type (allowed: PDF, DOC, DOCX, XLS, XLSX)');
        if (!okSize) reasons.push('File too large (max 100MB per file)');
        invalid.push({ file, reasons });
      }
    });

    (fileRejections || []).forEach(({ file, errors }) => {
      const reasons = (errors || []).map(e => {
        if (e.code === 'file-invalid-type') return 'Invalid type (allowed: PDF, DOC, DOCX, XLS, XLSX)';
        if (e.code === 'file-too-large') return 'File too large (max 100MB per file)';
        return e.message || 'Rejected';
      });
      invalid.push({ file, reasons });
    });

    const proposed = dedupeByNameAndSize([...uploadedFiles, ...valid]);

    if (totalSizeOf(proposed) > MAX_TOTAL_SIZE) {
      const totalReason = `Total upload limit exceeded (max ${FormatFileSize(MAX_TOTAL_SIZE)} total)`;
      const overflowRejected = valid.map(file => ({ file, reasons: [totalReason] }));

      setRejectedFiles(prev => mergeRejected(prev, [...invalid, ...overflowRejected]));

      setDocError(totalReason);
      return;
    }

    setUploadedFiles(proposed);
    formikRef?.current?.setFieldValue('file', proposed);
    formikRef?.current?.setFieldTouched('file', true, false);

    if (invalid.length) {
      setRejectedFiles(prev => mergeRejected(prev, invalid));
      setDocError('One or more files were rejected. Only PDF, DOC, DOCX, XLS, XLSX under 100MB each are allowed.');
    } else {
      setDocError(undefined);
    }
  };

  const removeFile = (index) => {
    const updated = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updated);
    formikRef?.current?.setFieldValue('file', updated);

    if (totalSizeOf(updated) > MAX_TOTAL_SIZE) {
      formikRef?.current?.setFieldError('file', 'Total upload limit is 100MB. Remove some files.');
    } else {
      setDocError(undefined)
      formikRef?.current?.setFieldTouched('file', true, false);
    }
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    multiple: true,
    accept: acceptMap,
    maxSize: MAX_FILE_SIZE, // 2MB per file
  });

  return (
    <div className="z-50 flex items-center justify-center w-full mb-5">
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, formikHelpers) => onSubmit(values, formikHelpers)}
      >
        {({ values, setFieldValue, setFieldTouched, validateField, handleSubmit, errors }) => (
          console.log('errors', errors),

          <Form
            onSubmit={handleSubmit}
            className="scrollable-y-auto bg-[--tw-navbar-bg] dark:bg-[--tw-navbar-bg-dark] border border-gray-300 dark:border-t-light dark:border-light p-6 rounded-lg shadow-lg w-full relative"
          >
            <div className="w-full">
              <label className="mb-1 form-label">
                Subject <span className="pl-px text-red-600">*</span>
              </label>
              <Field name="subject" className="input" />
              <ErrorMessage name="subject" component="div" className="mt-1 text-xs text-red-500" />
            </div>

            <div className="w-full my-3">
              <label className="mb-1 form-label">
                Message <span className="pl-px text-red-600">*</span>
              </label>
              <Field
                name="message"
                as="textarea"
                rows="4"
                cols="50"
                className="min-h-[120px] input py-3 leading-[1.4em]"
              />
              <ErrorMessage name="message" component="div" className="mt-1 text-xs text-red-500" />
            </div>

            <div className="flex items-center justify-between gap-3 mt-4 min-h-[24px]">
              <div className="flex items-center">
                <Field name="sendType">
                  {({ field, form }) => (
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-1 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name="sendType"
                          value="email"
                          checked={form.values.sendType === 'email'}
                          className="accent-primary size-3.5"
                          onChange={() => {
                            toggleSendType(form.values, form.setFieldValue, form.setFieldTouched, form.validateField);
                          }}
                        />
                        Email
                      </label>

                      <label className="flex items-center gap-1 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name="sendType"
                          value="message"
                          checked={form.values.sendType === 'message'}
                          disabled={form.values.sendType === 'message'}
                          className="accent-primary size-3.5"
                        />
                        SMS
                        <span className="leading-none badge badge-xs badge-pill badge-outline badge-primary">
                          Coming soon
                        </span>
                      </label>
                    </div>
                  )}
                </Field>
              </div>

              {values.sendType === 'email' && (
                <div {...getRootProps()}>
                  <input {...getInputProps()} />
                  <button
                    type="button"
                    onClick={open}
                    className="text-sm leading-none w-fit text-primary btn btn-link btn-sm"
                  >
                    Upload Document
                  </button>
                </div>
              )}
            </div>

            {values.sendType === 'email' && (uploadedFiles.length > 0 || rejectedFiles.length > 0) && (
              <>
                <div className="flex flex-col gap-2 max-h-40 overflow-y-auto mt-2.5">
                  {uploadedFiles.map((file, index) => (
                    <div
                      key={`valid-${file.name}-${file.size}-${index}`}
                      className="md:w-full w-full flex items-center justify-between border border-primary p-3 rounded-md bg-primary-light"
                    >
                      <div className="flex flex-col max-w-[70%]">
                        <p className="text-sm truncate">{file.name}</p>
                        <span className="text-xs text-gray-600">Size: {FormatFileSize(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="ml-4 text-sm underline text-danger"
                      >
                        Remove
                      </button>
                    </div>
                  ))}

                  {rejectedFiles.map(({ file, reasons }, index) => (
                    <div
                      key={`rejected-${file.name}-${file.size}-${index}`}
                      className="md:w-full w-full flex items-center justify-between border border-danger p-3 rounded-md bg-danger-light"
                    >
                      <div className="flex flex-col max-w-[70%]">
                        <p className="text-sm truncate text-red-600">{file.name}</p>
                        <span className="text-xs text-red-500">Size: {FormatFileSize(file.size)}</span>
                        {reasons?.length > 0 && (
                          <ul className="mt-1 text-[11px] text-red-500 list-disc pl-4">
                            {reasons.map((r, i) => (
                              <li key={i}>{r}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setRejectedFiles(prev => prev.filter((_, i) => i !== index))}
                        className="ml-4 text-sm underline text-danger"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>

                <ErrorMessage name="file" component="div" className="text-red-500 text-[11px] mt-1" />
              </>
            )}

            <button type="submit" disabled={isLoading || rejectedFiles.length > 0} className="justify-center w-full mt-4 btn btn-primary">
              {isLoading ? 'Submiting...' : 'Submit'}
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export { ContactMessagesTab };